import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  PersonOutline as PersonIcon,
  FolderOpen as FolderIcon,
  Map as MapIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  PendingActions as PendingActionsIcon,
  History as HistoryIcon,
  Engineering as EngineeringIcon,
  ExpandLess,
  ExpandMore,
  Apartment as ApartmentIcon,
  Group as GroupIcon,
  BarChart as ReportsIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../types';

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
  drawerWidth: string | number;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onClose, drawerWidth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const toggleGroup = (group: string) => {
    setOpenGroup((prev) => (prev === group ? null : group));
  };

  const navGroups = [
    {
      key: 'overview',
      label: 'Overview',
      icon: <DashboardIcon fontSize="small" />,
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
        { text: 'Pending Actions', icon: <PendingActionsIcon />, path: '/pending-actions', roles: [Role.SUPER_ADMIN] },
        { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
      ],
    },
    {
      key: 'realEstate',
      label: 'Real Estate',
      icon: <ApartmentIcon fontSize="small" />,
      items: [
        { text: 'Properties', icon: <HomeIcon />, path: '/properties', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
        { text: 'Projects', icon: <FolderIcon />, path: '/projects', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
        { text: 'Map', icon: <MapIcon />, path: '/map', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
      ],
    },
    {
      key: 'people',
      label: 'People',
      icon: <GroupIcon fontSize="small" />,
      items: [
        { text: 'Agents', icon: <PeopleIcon />, path: '/agents', roles: [Role.SUPER_ADMIN] },
        { text: 'Clients', icon: <PersonIcon />, path: '/clients', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
        { text: 'Building Caretakers', icon: <EngineeringIcon />, path: '/building-caretakers', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
        { text: 'Brokers', icon: <ReportProblemIcon />, path: '/brokers', roles: [Role.SUPER_ADMIN, Role.ADMIN] },
      ],
    },
    {
      key: 'reports',
      label: 'Reports',
      icon: <ReportsIcon fontSize="small" />,
      items: [
        { text: 'Activities', icon: <HistoryIcon />, path: '/activities', roles: [Role.SUPER_ADMIN] },
      ],
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawer = (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Mobile Header with Close Button */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            mt: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                height: 60,
                width: 140,
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src="/logo.jpg"
                alt="Stone Properties"
                sx={{
                  height: 60,
                  width: 'auto',
                  objectFit: 'contain',
                  filter: (theme) =>
                    theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                  imageRendering: 'crisp-edges',
                }}
              />
            </Box>
          </Box>
          <IconButton 
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      
      {/* Desktop Logo - shown above navigation in permanent sidebar */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: 64,
          }}
        >
          <Box
            sx={{
              height: 100,
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src="/logo.jpg"
              alt="Stone Properties"
              sx={{
                height: 150,
                width: 'auto',
                objectFit: 'contain',
                filter: (theme) =>
                  theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                marginTop: '-42px',
                marginBottom: '-42px',
                imageRendering: 'crisp-edges',
              }}
            />
          </Box>
        </Box>
      )}

      {/* Navigation Section */}
      <List sx={{ flex: 1, px: 1.5, overflowY: 'auto', py: 1 }}>
        {navGroups.map((group) => {
          const visibleItems = group.items.filter((item) =>
            item.roles.includes(user?.role as Role)
          );
          if (visibleItems.length === 0) return null;

          return (
            <Box key={group.key} sx={{ mb: 0.5 }}>
              {/* Group Header */}
              <ListItemButton
                onClick={() => toggleGroup(group.key)}
                sx={{
                  borderRadius: 1.5,
                  py: 0.75,
                  px: 1.5,
                  mb: 0.25,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                  {group.icon}
                </ListItemIcon>
                <ListItemText
                  primary={group.label}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    color: 'text.secondary',
                  }}
                />
                {openGroup === group.key ? (
                  <ExpandLess sx={{ fontSize: 16, color: 'text.secondary' }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 16, color: 'text.secondary' }} />
                )}
              </ListItemButton>

              {/* Group Items */}
              <Collapse in={openGroup === group.key} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {visibleItems.map((item) => {
                    const isSelected =
                      location.pathname === item.path ||
                      (item.path !== '/' && location.pathname.startsWith(item.path));

                    return (
                      <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            borderRadius: 1.5,
                            py: 1,
                            pl: 3.5,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              '& .MuiListItemIcon-root': { color: 'white' },
                              '&:hover': { backgroundColor: 'primary.dark' },
                            },
                            '&:hover': {
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'rgba(0,0,0,0.06)'
                                  : 'rgba(255,255,255,0.08)',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: '0.9rem',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>

              <Divider sx={{ my: 0.5, opacity: 0.4 }} />
            </Box>
          );
        })}
      </List>
      
      <Divider />
      
      {/* Footer Info */}
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Stone Properties © 2026
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer - Slides in from left, closeable */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: typeof drawerWidth === 'number' ? drawerWidth : '280px',
            maxWidth: '85vw', // Don't take full width on small phones
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#ffffff' : theme.palette.grey[800],
            boxShadow: '4px 0 12px rgba(0, 0, 0, 0.15)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Desktop Drawer - Permanent, takes percentage of screen */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#ffffff' : theme.palette.grey[800],
            borderRight: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[700]}`,
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;
